import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Users, Search, Filter, MoreHorizontal, UserCheck, UserX, 
  Ban, Shield, Key, Trash2, AlertTriangle, Calendar,
  Mail, MapPin, Eye, Edit, CheckCircle, XCircle, Send, Loader2
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

export default function AdminUsers() {
  const { adminAPI } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [userSecurity, setUserSecurity] = useState({});
  const [securityLoading, setSecurityLoading] = useState({});
  const [actionMessage, setActionMessage] = useState(null);
  const [emailSending, setEmailSending] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [usersPerPage] = useState(20);

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        skip: currentPage * usersPerPage,
        limit: usersPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status_filter: statusFilter })
      };
      
      const data = await adminAPI.getUsers(params);
      setUsers(data);
      
      // Load security info for each user
      for (const user of data) {
        loadUserSecurity(user.id);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSecurity = async (userId) => {
    try {
      setSecurityLoading(prev => ({ ...prev, [userId]: true }));
      const security = await adminAPI.getUserSecurity(userId);
      setUserSecurity(prev => ({ ...prev, [userId]: security }));
    } catch (error) {
      console.error(`Failed to load security for user ${userId}:`, error);
    } finally {
      setSecurityLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleResendVerification = async (userId, e) => {
    e.stopPropagation();
    try {
      setSecurityLoading(prev => ({ ...prev, [userId]: true }));
      await adminAPI.resendVerificationEmail(userId);
      setActionMessage({ type: 'success', text: 'Email de vérification envoyé !' });
      setTimeout(() => setActionMessage(null), 3000);
    } catch (error) {
      setActionMessage({ type: 'error', text: error.message || 'Erreur lors de l\'envoi' });
      setTimeout(() => setActionMessage(null), 3000);
    } finally {
      setSecurityLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleVerifyManually = async (userId, e) => {
    e.stopPropagation();
    try {
      setSecurityLoading(prev => ({ ...prev, [userId]: true }));
      await adminAPI.verifyUserEmailManually(userId);
      setUserSecurity(prev => ({ ...prev, [userId]: { ...prev[userId], email_verified: true } }));
      setActionMessage({ type: 'success', text: 'Email vérifié manuellement !' });
      await loadUsers();
      setTimeout(() => setActionMessage(null), 3000);
    } catch (error) {
      setActionMessage({ type: 'error', text: error.message || 'Erreur lors de la vérification' });
      setTimeout(() => setActionMessage(null), 3000);
    } finally {
      setSecurityLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleUserAction = async (action, userId, data = {}) => {
    try {
      setActionLoading(true);
      
      switch (action) {
        case 'suspend':
          await adminAPI.suspendUser(userId, data);
          break;
        case 'ban':
          await adminAPI.banUser(userId, data);
          break;
        case 'unban':
          await adminAPI.unbanUser(userId);
          break;
        case 'sendResetEmail':
          await adminAPI.sendPasswordResetEmail(userId);
          setActionMessage({ type: 'success', text: 'Email de réinitialisation envoyé avec succès !' });
          break;
        case 'resetPasswordManual':
          await adminAPI.resetUserPassword(userId, data);
          setActionMessage({ type: 'success', text: 'Mot de passe réinitialisé avec succès !' });
          break;
        case 'delete':
          await adminAPI.deleteUser(userId);
          break;
        default:
          throw new Error('Action non reconnue');
      }
      
      // Recharger les utilisateurs
      await loadUsers();
      setShowUserModal(false);
      
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      alert(`Erreur lors de l'action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { class: 'bg-blue-100 text-blue-800', label: 'En attente' },
      active: { class: 'bg-green-100 text-green-800', label: 'Actif' },
      suspended: { class: 'bg-yellow-100 text-yellow-800', label: 'Suspendu' },
      banned: { class: 'bg-red-100 text-red-800', label: 'Banni' },
      deleted: { class: 'bg-gray-100 text-gray-800', label: 'Supprimé' }
    };
    
    const variant = variants[status] || variants.active;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      user: { class: 'bg-blue-100 text-blue-800', label: 'Utilisateur' },
      moderator: { class: 'bg-purple-100 text-purple-800', label: 'Modérateur' },
      admin: { class: 'bg-red-100 text-red-800', label: 'Admin' }
    };
    
    const variant = variants[role] || variants.user;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  const UserActionModal = ({ user, onAction }) => {
    const [actionType, setActionType] = useState('');
    const [reason, setReason] = useState('');
    const [suspendUntil, setSuspendUntil] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      
      if (actionType === 'resetPasswordManual' && newPassword !== confirmPassword) {
        alert('Les mots de passe ne correspondent pas');
        return;
      }
      
      const actionData = {
        reason,
        ...(actionType === 'suspend' && { suspended_until: new Date(suspendUntil).toISOString() }),
        ...(actionType === 'resetPasswordManual' && { new_password: newPassword, user_id: user.id })
      };

      onAction(actionType, user.id, actionData);
    };

    return (
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Actions sur l'utilisateur</DialogTitle>
          <DialogDescription>
            Utilisateur: @{user.username} ({user.email})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Action à effectuer</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une action" />
              </SelectTrigger>
              <SelectContent>
                {user.user_status === 'pending' && (
                  <SelectItem value="activate">Activer le compte</SelectItem>
                )}
                {user.user_status === 'active' && (
                  <>
                    <SelectItem value="suspend">Suspendre</SelectItem>
                    <SelectItem value="ban">Bannir</SelectItem>
                  </>
                )}
                {(user.user_status === 'suspended' || user.user_status === 'banned') && (
                  <SelectItem value="unban">Débannir/Réactiver</SelectItem>
                )}
                <SelectItem value="sendResetEmail">📧 Envoyer lien de réinitialisation</SelectItem>
                <SelectItem value="resetPasswordManual">🔑 Réinitialiser manuellement</SelectItem>
                <SelectItem value="delete">Supprimer compte</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {actionType === 'sendResetEmail' && (
            <Alert className="bg-blue-50 border-blue-200">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Un email contenant un lien de réinitialisation sera envoyé à <strong>{user.email}</strong>. 
                L'utilisateur pourra choisir son nouveau mot de passe.
              </AlertDescription>
            </Alert>
          )}

          {actionType === 'resetPasswordManual' && (
            <div className="space-y-3">
              <Alert className="bg-yellow-50 border-yellow-200">
                <Key className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Vous allez définir un nouveau mot de passe manuellement. 
                  Pensez à communiquer ce mot de passe à l'utilisateur de manière sécurisée.
                </AlertDescription>
              </Alert>
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Entrez le nouveau mot de passe"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label>Confirmer le mot de passe</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez le mot de passe"
                  required
                  minLength={8}
                />
              </div>
            </div>
          )}

          {actionType === 'suspend' && (
            <div>
              <Label>Suspendu jusqu'au</Label>
              <Input
                type="datetime-local"
                value={suspendUntil}
                onChange={(e) => setSuspendUntil(e.target.value)}
                required
              />
            </div>
          )}

          {(actionType === 'suspend' || actionType === 'ban' || actionType === 'delete') && (
            <div>
              <Label>Raison {actionType !== 'delete' ? '(obligatoire)' : '(optionnel)'}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez la raison de cette action"
                required={actionType !== 'delete'}
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              disabled={!actionType || actionLoading || (actionType === 'resetPasswordManual' && (!newPassword || newPassword !== confirmPassword))}
              variant={actionType === 'delete' || actionType === 'ban' ? 'destructive' : actionType === 'sendResetEmail' ? 'default' : 'default'}
              className={`flex-1 ${actionType === 'sendResetEmail' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            >
              {actionLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  En cours...
                </>
              ) : actionType === 'sendResetEmail' ? (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer l'email
                </>
              ) : actionType === 'resetPasswordManual' ? (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Réinitialiser
                </>
              ) : (
                'Confirmer'
              )}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <p className="text-gray-600 mt-1">Gérer tous les utilisateurs de la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-600">{users.length} utilisateurs</span>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, email ou pseudo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="suspended">Suspendus</SelectItem>
                  <SelectItem value="banned">Bannis</SelectItem>
                  <SelectItem value="deleted">Supprimés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Utilisateurs</CardTitle>
          <CardDescription>
            Cliquez sur un utilisateur pour voir les détails et effectuer des actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Action message */}
          {actionMessage && (
            <Alert className={`mb-4 ${actionMessage.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
              <AlertDescription className={actionMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                {actionMessage.text}
              </AlertDescription>
            </Alert>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement des utilisateurs...</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowUserModal(true);
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                      {user.username?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">@{user.username}</h3>
                        {getRoleBadge(user.role || 'user')}
                        {getStatusBadge(user.user_status || 'active')}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </span>
                        {/* Email verification status */}
                        {userSecurity[user.id] !== undefined && (
                          <span className={`flex items-center gap-1 ${userSecurity[user.id]?.email_verified ? 'text-green-600' : 'text-orange-500'}`}>
                            {userSecurity[user.id]?.email_verified ? (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                <span>Vérifié</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3" />
                                <span>Non vérifié</span>
                              </>
                            )}
                          </span>
                        )}
                        {user.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {user.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      {/* Email verification actions */}
                      {userSecurity[user.id] && !userSecurity[user.id]?.email_verified && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleResendVerification(user.id, e)}
                            disabled={securityLoading[user.id]}
                            className="text-xs"
                          >
                            {securityLoading[user.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Send className="h-3 w-3 mr-1" />
                            )}
                            Renvoyer email
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleVerifyManually(user.id, e)}
                            disabled={securityLoading[user.id]}
                            className="text-xs text-green-600 border-green-200 hover:bg-green-50"
                          >
                            {securityLoading[user.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            )}
                            Valider manuellement
                          </Button>
                        </div>
                      )}
                      {user.ban_reason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <strong>Raison du bannissement:</strong> {user.ban_reason}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-right text-sm text-gray-600">
                      <div>{user.spots_created_count || 0} spots</div>
                      <div>{user.visits_count || 0} visites</div>
                    </div>
                    <MoreHorizontal className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}

              {users.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun utilisateur trouvé</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            >
              Précédent
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage + 1}
            </span>
            <Button
              variant="outline"
              disabled={users.length < usersPerPage}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Suivant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Action Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        {selectedUser && (
          <UserActionModal 
            user={selectedUser} 
            onAction={handleUserAction}
          />
        )}
      </Dialog>
    </div>
  );
}